import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wifi, WifiOff, CloudUpload, Loader2 } from "lucide-react";
import { subscribe, drainQueue, installSyncWorker } from "@/lib/syncWorker";

/**
 * Compact connectivity + offline-queue badge for the Topbar.
 *
 *  - Offline:        red "Offline" pill.
 *  - Pending sync:   amber "N queued" pill, click → /dispatch (or trigger drain).
 *  - Syncing:        animated spinner.
 *  - Online & idle:  hidden (no visual noise on the happy path).
 */
export default function OfflineStatus() {
  const navigate = useNavigate();
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [state, setState] = useState({ pending: 0, syncing: false });

  useEffect(() => {
    installSyncWorker();
    const off = subscribe(setState);
    const goOn = () => setOnline(true);
    const goOff = () => setOnline(false);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    return () => {
      off();
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
    };
  }, []);

  if (online && state.pending === 0 && !state.syncing) return null;

  if (!online) {
    return (
      <button
        data-testid="offline-pill"
        onClick={() => navigate("/dispatch")}
        className="flex items-center gap-1.5 rounded-sm border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
        title="No network. Labels can still be printed from cache; new RTO / tracking inputs will be queued."
      >
        <WifiOff size={12} />
        Offline{state.pending > 0 ? ` · ${state.pending} queued` : ""}
      </button>
    );
  }

  if (state.syncing) {
    return (
      <span
        data-testid="syncing-pill"
        className="flex items-center gap-1.5 rounded-sm border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700"
      >
        <Loader2 size={12} className="animate-spin" />
        Syncing{state.pending > 0 ? ` · ${state.pending}` : ""}
      </span>
    );
  }

  // online & idle but queue has items (e.g. failed rows after 5 retries)
  return (
    <button
      data-testid="queue-pill"
      onClick={() => drainQueue()}
      className="flex items-center gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
      title="Pending offline mutations. Click to retry."
    >
      <CloudUpload size={12} />
      {state.pending} queued · retry
    </button>
  );
}

export function OnlineDot() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const goOn = () => setOnline(true);
    const goOff = () => setOnline(false);
    window.addEventListener("online", goOn);
    window.addEventListener("offline", goOff);
    return () => {
      window.removeEventListener("online", goOn);
      window.removeEventListener("offline", goOff);
    };
  }, []);
  return online ? (
    <span data-testid="online-dot" className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
      <Wifi size={11} /> Online
    </span>
  ) : (
    <span data-testid="offline-dot" className="inline-flex items-center gap-1 text-[11px] text-rose-700">
      <WifiOff size={11} /> Offline
    </span>
  );
}
